import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"
import L from "leaflet"
import { showToast } from "../lib/toast"
import { track } from "../lib/mixpanel"

// External-integration dashboard (/integrations/:provider — CBRE pilot).
// Handles the export upload (presign → S3 PUT → register → poll), the
// monthly-starts chart, client-side search/filter/sort/pagination over the
// server-rendered project table, and the market-pin map view (same Leaflet
// styling as the portfolio map). When an extraction finishes we do a full
// reload (the whole page is derived from the new dataset) with the toast
// persisted via sessionStorage, same pattern as fee_schedule.
export default class extends Controller {
  static targets = ["modal", "modalDropzone", "dropzone", "fileInput", "progress", "progressWrap",
                    "error", "search", "filterForm", "count", "tbody",
                    "showMoreWrap", "showMoreButton", "monthlyCanvas",
                    "tableWrap", "mapWrap", "mapContainer", "tableViewButton", "mapViewButton"]
  static values = {
    presignUrl: String,
    createUrl: String,
    statusUrl: String,
    filtersUrl: String,
    savedFilters: Object,
    processingId: Number,
    provider: String,
    monthly: Array
  }

  static PAGE_SIZE = 100
  static PAGE_STEP = 200

  connect() {
    this._csrf = document.querySelector('meta[name="csrf-token"]')?.content
    this._polling = null
    this._visibleLimit = this.constructor.PAGE_SIZE
    this._showPendingFlash()
    this._initChart()
    this._restoreSavedFilters()
    if (this.hasTbodyTarget) this._applyFilters()
    if (this.processingIdValue) this._poll(this.processingIdValue)
  }

  // Reopen the page in the view (table/map) the user left it in. The filter
  // dropdowns themselves are applied server-side before render.
  _restoreSavedFilters() {
    const saved = this.savedFiltersValue || {}
    if (saved.view === "map" && this.hasMapWrapTarget) this.showMap({ persist: false })
  }

  // Only the table/map view choice persists via PATCH (no page reload on
  // toggle). Filter choices persist on the GET that applies them.
  _persistFilters() {
    if (!this.filtersUrlValue) return
    clearTimeout(this._persistTimer)
    this._persistTimer = setTimeout(() => {
      fetch(this.filtersUrlValue, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrf },
        body: JSON.stringify({ view: this._currentView || "table" })
      }).catch(() => {})
    }, 400)
  }

  // ── Top filter bar (GET form — server re-renders everything) ────────────

  submitFilters(event) {
    // Checkbox dropdowns (PMs, statuses) apply via their Apply button.
    if (["pms[]", "statuses[]"].includes(event?.target?.name)) return
    if (this.hasFilterFormTarget) this.filterFormTarget.requestSubmit()
  }

  applyPmFilter() {
    if (this.hasFilterFormTarget) this.filterFormTarget.requestSubmit()
  }

  clearPmFilter() {
    this._clearChecks("pms[]")
  }

  clearStatusFilter() {
    this._clearChecks("statuses[]")
  }

  _clearChecks(name) {
    if (!this.hasFilterFormTarget) return
    this.filterFormTarget.querySelectorAll(`input[name="${name}"]`).forEach((box) => { box.checked = false })
    this.filterFormTarget.requestSubmit()
  }

  disconnect() {
    if (this._polling) clearInterval(this._polling)
    this._stopAiProgress()
    if (this._chart) this._chart.destroy()
    if (this._map) {
      this._map.remove()
      this._map = null
    }
  }

  // ── Upload modal ─────────────────────────────────────────────────────────

  openModal() {
    if (this.hasModalTarget) this.modalTarget.classList.remove("hidden")
  }

  hideModal() {
    if (this.hasModalTarget) this.modalTarget.classList.add("hidden")
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  // ── Drag & drop / file pick (inline hero zone + modal zone) ──────────────

  dragover(event) {
    event.preventDefault()
    event.currentTarget.classList.add("border-brand", "bg-blue-50")
  }

  dragleave(event) {
    event.currentTarget.classList.remove("border-brand", "bg-blue-50")
  }

  drop(event) {
    event.preventDefault()
    event.currentTarget.classList.remove("border-brand", "bg-blue-50")
    const file = event.dataTransfer?.files?.[0]
    if (file) this._handleFile(file)
  }

  pick() {
    this.fileInputTarget.click()
  }

  fileChosen(event) {
    const file = event.target.files?.[0]
    if (file) this._handleFile(file)
    event.target.value = ""
  }

  // ── Upload → extract → poll ──────────────────────────────────────────────

  async _handleFile(file) {
    const ext = (file.name.split(".").pop() || "").toLowerCase()
    if (!["xlsx", "xls", "csv"].includes(ext)) {
      this._setError("Please upload an Excel (.xlsx, .xls) or CSV file.")
      return
    }
    this.hideModal()
    this._setError("")
    this._startAiProgress()
    try {
      const cacheName = await this._presignAndPut(file)
      const id = await this._register(file, cacheName)
      this._poll(id)
    } catch (e) {
      this._stopAiProgress()
      this._setError(e.message || "Upload failed. Please try again.")
    }
  }

  async _presignAndPut(file) {
    const presign = await fetch(this.presignUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrf },
      body: JSON.stringify({
        files: [{ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size }]
      })
    })
    const pj = await presign.json()
    if (!presign.ok) throw new Error(pj.error || "Could not start the upload.")
    const up = pj.uploads[0]
    const put = await fetch(up.upload_url, { method: "PUT", headers: { "Content-Type": up.content_type }, body: file })
    if (!put.ok) throw new Error("Upload to storage failed.")
    return up.cache_name
  }

  async _register(file, cacheName) {
    const resp = await fetch(this.createUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrf },
      body: JSON.stringify({ cache_name: cacheName, filename: file.name, content_type: file.type })
    })
    const data = await resp.json()
    if (!resp.ok) throw new Error(data.error || "Could not start processing.")
    return data.id
  }

  _poll(id) {
    if (this._polling) clearInterval(this._polling)
    this._startAiProgress()
    this._polling = setInterval(async () => {
      try {
        const resp = await fetch(`${this.statusUrlValue}?id=${id}`, { headers: { Accept: "application/json" } })
        const data = await resp.json()
        if (data.status === "done") {
          clearInterval(this._polling)
          const n = data.project_count
          sessionStorage.setItem("integration_dashboard_flash",
            `Dashboard updated — ${n} project${n === 1 ? "" : "s"} loaded.`)
          window.location.reload()
        } else if (data.status === "failed") {
          clearInterval(this._polling)
          this._stopAiProgress()
          this._setError(data.error || "We couldn't read that file. Please try another export.")
        }
      } catch (e) {
        // transient network error — keep polling
      }
    }, 3000)
  }

  // Specs-style AI banner with elapsed timer, stages advancing over time.
  _startAiProgress() {
    if (this.hasProgressWrapTarget) this.progressWrapTarget.classList.remove("hidden")
    if (!this.hasProgressTarget) return
    this._aiStart = performance.now()
    const stages = [
      [0,  "Reading the file..."],
      [6,  "Mapping the columns..."],
      [14, "Organizing projects by phase, region and work type..."],
      [30, "Building the dashboard..."],
      [50, "Still working — bigger file, almost there..."]
    ]
    const render = () => {
      const secs = Math.round((performance.now() - this._aiStart) / 1000)
      let msg = stages[0][1]
      for (const [at, text] of stages) { if (secs >= at) msg = text }
      this.progressTarget.innerHTML =
        `<span class="font-bold ai-rainbow-text">ConsLog Integration — ${msg}</span> ` +
        `<span class="text-gray-500 font-normal tabular-nums inline-block w-8 text-left">${secs}s</span>`
    }
    render()
    clearInterval(this._aiTimerInterval)
    this._aiTimerInterval = setInterval(render, 1000)
  }

  _stopAiProgress() {
    clearInterval(this._aiTimerInterval)
    this._aiTimerInterval = null
    if (this.hasProgressWrapTarget) this.progressWrapTarget.classList.add("hidden")
  }

  _setError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.classList.toggle("hidden", !message)
  }

  _showPendingFlash() {
    const msg = sessionStorage.getItem("integration_dashboard_flash")
    if (!msg) return
    sessionStorage.removeItem("integration_dashboard_flash")
    showToast(msg)
  }

  // ── Monthly starts chart ─────────────────────────────────────────────────

  _initChart() {
    if (!this.hasMonthlyCanvasTarget || !this.monthlyValue.length) return
    // Same per-company theme accent the portfolio dashboard charts use
    // (set by portfolio_theme_style_tag in the authenticated layout).
    const accent = getComputedStyle(document.documentElement)
      .getPropertyValue("--portfolio-accent").trim() || "#fb923c"
    this._chart = new Chart(this.monthlyCanvasTarget, {
      type: "bar",
      data: {
        labels: this.monthlyValue.map((m) => m.label),
        datasets: [{
          data: this.monthlyValue.map((m) => m.count),
          backgroundColor: accent,
          borderRadius: 3,
          maxBarThickness: 26
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { ticks: { autoSkip: false, maxRotation: 45, color: "#6b7280", font: { size: 11 } }, grid: { display: false } },
          y: { beginAtZero: true, ticks: { color: "#6b7280", font: { size: 11 }, precision: 0 }, grid: { color: "rgba(107,114,128,0.12)" } }
        }
      }
    })
  }

  // ── Table: search / filter / paginate ────────────────────────────────────

  filterTable() {
    this._visibleLimit = this.constructor.PAGE_SIZE
    this._applyFilters()
  }

  showMore() {
    this._visibleLimit += this.constructor.PAGE_STEP
    this._applyFilters()
  }

  // Client-side column sort, portfolio-table style (click a header, click
  // again to flip direction). Rows carry data-sort-* attributes.
  sortTable(event) {
    const key = event.currentTarget.dataset.sortKey
    if (!key) return
    this._sortDir = this._sortKey === key && this._sortDir === "asc" ? "desc" : "asc"
    this._sortKey = key

    const numeric = ["budget", "final"].includes(key)
    const dir = this._sortDir === "asc" ? 1 : -1
    const rows = Array.from(this.tbodyTarget.rows)
    rows.sort((a, b) => {
      const av = a.dataset[`sort${key.charAt(0).toUpperCase()}${key.slice(1)}`] || ""
      const bv = b.dataset[`sort${key.charAt(0).toUpperCase()}${key.slice(1)}`] || ""
      if (numeric) return (parseFloat(av) - parseFloat(bv)) * dir
      // Blank cells (missing dates, PMs) always sink to the bottom.
      if (av === "" && bv === "") return 0
      if (av === "") return 1
      if (bv === "") return -1
      return av.localeCompare(bv) * dir
    })
    rows.forEach((r) => this.tbodyTarget.appendChild(r))
    this._applyFilters()
  }

  // ── Table / map view toggle ──────────────────────────────────────────────

  showTable() {
    if (!this.hasMapWrapTarget) return
    this.mapWrapTarget.classList.add("hidden")
    this.tableWrapTarget.classList.remove("hidden")
    this._setViewButtons(this.tableViewButtonTarget, this.mapViewButtonTarget)
    this._currentView = "table"
    this._persistFilters()
  }

  showMap(options = {}) {
    if (!this.hasMapWrapTarget) return
    this.tableWrapTarget.classList.add("hidden")
    this.mapWrapTarget.classList.remove("hidden")
    this._setViewButtons(this.mapViewButtonTarget, this.tableViewButtonTarget)
    this._currentView = "map"

    if (!this._map && this.hasMapContainerTarget) {
      this._initLeafletMap()
      track("Integration Map Viewed", { provider: this.providerValue })
    } else if (this._map) {
      this._map.invalidateSize()
    }
    if (options.persist !== false) this._persistFilters()
  }

  _setViewButtons(activeBtn, inactiveBtn) {
    activeBtn.classList.add("bg-white", "shadow-sm", "text-gray-900")
    activeBtn.classList.remove("text-gray-500", "hover:text-gray-700")
    inactiveBtn.classList.remove("bg-white", "shadow-sm", "text-gray-900")
    inactiveBtn.classList.add("text-gray-500", "hover:text-gray-700")
  }

  // Market pins on the same CARTO light tiles as the portfolio map; pin
  // radius scales with the market's project count.
  _initLeafletMap() {
    let markers = []
    try {
      markers = JSON.parse(this.mapWrapTarget.dataset.markers || "[]")
    } catch (e) {
      markers = []
    }
    if (!markers.length) return

    const accent = getComputedStyle(document.documentElement)
      .getPropertyValue("--portfolio-accent").trim() || "#fb923c"

    this._map = L.map(this.mapContainerTarget, {
      scrollWheelZoom: false,
      zoomControl: true,
      attributionControl: false
    })
    // Esri World Light Gray (base + labels). Was CARTO Positron until
    // 2026-08-27, when CARTO started watermarking keyless tiles with
    // "API KEY REQUIRED"; Esri's equivalent needs no key. Same swap as
    // portfolio_map_controller.
    L.tileLayer("https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}", {
      maxZoom: 19,
      maxNativeZoom: 16
    }).addTo(this._map)
    L.tileLayer("https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}", {
      maxZoom: 19,
      maxNativeZoom: 16,
      pane: "overlayPane"
    }).addTo(this._map)

    const money = (v) => "$" + Math.round(v).toLocaleString()
    const circles = markers.map((m) => {
      const radius = Math.min(6 + Math.sqrt(m.count) * 2.2, 26)
      const circle = L.circleMarker([m.lat, m.lng], {
        radius,
        color: "#ffffff",
        weight: 1.5,
        fillColor: accent,
        fillOpacity: 0.8
      })
      circle.bindPopup(`
        <div class="portfolio-map-popup">
          <div class="portfolio-map-popup-title">${this._escape(m.market)}</div>
          <div class="portfolio-map-popup-meta">${this._escape(m.region || "")}</div>
          <div class="portfolio-map-popup-row"><span class="portfolio-map-popup-label">Projects</span> ${m.count.toLocaleString()}</div>
          <div class="portfolio-map-popup-row"><span class="portfolio-map-popup-label">Budget</span> ${money(m.budget)}</div>
        </div>
      `, { maxWidth: 280, minWidth: 200 })
      return circle
    })

    const group = L.featureGroup(circles).addTo(this._map)
    if (circles.length === 1) {
      this._map.setView(circles[0].getLatLng(), 10)
    } else {
      this._map.fitBounds(group.getBounds().pad(0.15))
    }
    setTimeout(() => this._map && this._map.invalidateSize(), 50)
  }

  _escape(str) {
    const div = document.createElement("div")
    div.textContent = str == null ? "" : String(str)
    return div.innerHTML
  }

  // Quick text search over the (already server-filtered) rows.
  _applyFilters() {
    const q = (this.hasSearchTarget ? this.searchTarget.value : "").trim().toLowerCase()

    let matches = 0
    let shown = 0
    for (const row of this.tbodyTarget.rows) {
      const hit = !q || row.dataset.search.includes(q)
      if (hit) matches++
      const visible = hit && matches <= this._visibleLimit
      if (visible) shown++
      row.classList.toggle("hidden", !visible)
    }

    if (this.hasCountTarget) {
      this.countTarget.textContent = shown === matches
        ? `${matches.toLocaleString()} project${matches === 1 ? "" : "s"}`
        : `Showing ${shown.toLocaleString()} of ${matches.toLocaleString()}`
    }
    if (this.hasShowMoreWrapTarget) {
      this.showMoreWrapTarget.classList.toggle("hidden", matches <= this._visibleLimit)
      if (this.hasShowMoreButtonTarget && matches > this._visibleLimit) {
        const remaining = matches - this._visibleLimit
        this.showMoreButtonTarget.textContent =
          `Show ${Math.min(remaining, this.constructor.PAGE_STEP).toLocaleString()} more (${remaining.toLocaleString()} remaining)`
      }
    }
  }
}
